import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Info.module.scss";
import Input from "../../components/common/input/Input";
import Button from "../../components/common/button/Button";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import ProfileService from "../../api/services/profile.service";

const Info = () => {
  const { t } = useTranslation("translation");
  const [logo, setLogo] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await ProfileService.getProfile();
        const data = res.data;
        setFirstName(data.first_name || "");
        setLastName(data.last_name || "");
        setPhone(data.phone || "");
        setEmail(data.email || "");
        setLogo(data.profile_photo || null);
      } catch (e) {
        // ignore for now; could add toast
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogo(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData();
    if (firstName) formData.append("first_name", firstName);
    if (lastName) formData.append("last_name", lastName);
    if (phone) formData.append("phone", phone);
    if (logoFile) formData.append("profile_photo", logoFile);

    try {
      setSaving(true);
      const res = await ProfileService.updateProfile(formData);
      const data = res.data;
      setFirstName(data.first_name || "");
      setLastName(data.last_name || "");
      setPhone(data.phone || "");
      setEmail(data.email || "");
      setLogo(data.profile_photo || null);
      setLogoFile(null);
    } catch (err) {
      // ignore for now; could add toast
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.store_info}>
      <TitleUpdater title={t("info.title") as string} />
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.add_logo}>
          <div>
            <div className={styles.store_logo} onClick={handleLogoClick}>
              {logo ? (
                <img src={logo} alt={t("info.logo_alt") as string} />
              ) : (
                <div className={styles.empty_logo}>
                  {t("info.click_to_add_logo") as string}
                </div>
              )}
            </div>
          </div>
        </div>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleLogoChange}
        />
        <div className={styles.inputs}>
          <div className={styles.input}>
            <Input
              label={t("info.form.name") as string}
              placeholder={t("info.form.placeholder_name") as string}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={loading || saving}
            />
          </div>
          <div className={styles.input}>
            <Input
              label={t("info.form.last_name") as string}
              placeholder={t("info.form.placeholder_last_name") as string}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={loading || saving}
            />
          </div>
          <div className={styles.input}>
            <Input
              label={t("info.form.phone_number") as string}
              placeholder={t("info.form.placeholder_phone") as string}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={loading || saving}
            />
          </div>
          <div className={styles.input}>
            <Input
              label={t("info.form.email") as string}
              placeholder={t("info.form.placeholder_email") as string}
              value={email}
              disabled
            />
          </div>
        </div>
        <div className={styles.submit}>
          <Button type="submit" disabled={saving || loading}>
            {saving
              ? (t("info.saving") as string)
              : (t("info.submit") as string)}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Info;
